import { useTranslation } from 'react-i18next';

import { HumanResourcesItem } from '@/app/models/detailsRH';
import { SubGroup } from '@/app/models/financalGroup';
import { useHumanResourcesTransferMutation } from '@/app/services/detailsHRApi';
import { useFinancialSubGroupQuery } from '@/app/services/financialGroupApi';
import DrawerButtons from '@/components/common/DrawerButtons';
import ModalPageTitle from '@/components/common/ModalPageTitle';
import { useFinancialSubGroup } from '@/context/FinancialSubGroup';
import { usePeriod } from '@/context/PeriodContext';
import { Box, FormControl, MenuItem, Select, Stack, TextField, Typography, useTheme } from '@mui/material';
import { useState } from 'react';
import { toast } from 'react-toastify';
import useFormattedCpfAndCpnj from '@/hooks/useFormattedCpfAndCpnj';

interface NewHumanResourcesTransferProps {
  selectedItem: HumanResourcesItem | null;
  setDrawerOpen: (open: boolean) => void;
  groupCode: number;
}

export default function NewHumanResourcesTransfer(props: NewHumanResourcesTransferProps) {
  const { t } = useTranslation(['humanResource', 'common']);
  const { selectedPeriod } = usePeriod();
  const { selectedItem, setDrawerOpen, groupCode } = props;
  const theme = useTheme();

  const { value: cpf } = useFormattedCpfAndCpnj(selectedItem?.cpf);

  const handleCancel = () => {
    setDrawerOpen(false);
  };
  const { selectedFinancialSubGroup } = useFinancialSubGroup();
  const [subGroup, setSubGroup] = useState<SubGroup>();

  const [transfer] = useHumanResourcesTransferMutation();
  const { data: financialsubGroup } = useFinancialSubGroupQuery({ groupCode, excludeRH: false });

  const handleSubmit = async () => {
    if (!selectedItem) return;
    setDrawerOpen(false);

    await toast.promise(
      transfer({
        id: selectedItem.id,
        financialSubGroupId: subGroup?.id as number,
        period: selectedPeriod?.date as string,
      }).unwrap(),
      {
        pending: t('common:processing.wait.a.moment') as string,
        success: t('transfer.success') as string,
        error: t('common:processing.failed') as string,
      },
      {
        position: toast.POSITION.TOP_RIGHT,
      },
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 30px)',
        maxHeight: 'calc(100vh - 30px)',
      }}
    >
      <Box sx={{ flexGrow: 1 }}>
        <ModalPageTitle title={t('transfer.detail')} />

        <Box
          sx={{
            display: 'block',
            flexDirection: 'row',
            mt: 2,
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'block', minWidth: theme.spacing(31), mb: 2 }}>
            <Box sx={{ mb: 2 }}>
              <Stack direction="row" sx={{ width: '100%', display: 'flex', gap: 2, alignItems: 'center' }}>
                <Typography variant="fieldHeader" sx={{ width: theme.spacing(12), mb: 0 }}>
                  {t('common:collaborator')}
                </Typography>
                <TextField
                  sx={{
                    flexGrow: 1,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                  size="small"
                  value={selectedItem?.name}
                  disabled
                />
              </Stack>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Stack direction="row" sx={{ width: '100%', display: 'flex', gap: 2, alignItems: 'center' }}>
                <Typography variant="fieldHeader" sx={{ width: theme.spacing(12), mb: 0 }}>
                  {t('cpf')}
                </Typography>
                <TextField
                  sx={{
                    flexGrow: 1,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                  size="small"
                  value={cpf}
                  disabled
                />
              </Stack>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Stack direction="row" sx={{ width: '100%', display: 'flex', gap: 2, alignItems: 'center' }}>
                <Typography variant="fieldHeader" sx={{ width: theme.spacing(12), mb: 0 }}>
                  {t('common:position')}
                </Typography>
                <TextField
                  sx={{
                    flexGrow: 1,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                  size="small"
                  value={selectedItem?.positionCode}
                  disabled
                />
              </Stack>
            </Box>
          </Box>
        </Box>
        <Box sx={{ width: '100%', height: theme.spacing(0.25), bgcolor: 'grey.300', mt: 2 }} />
      </Box>
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', mt: 2 }}>
        <Box sx={{ minWidth: theme.spacing(31) }}>
          <Typography component="p" variant="fieldHeader">
            {t('choose.the.group.to.transfer')}
          </Typography>
          <FormControl sx={{ width: theme.spacing(30) }}>
            <Select
              displayEmpty
              value={subGroup?.id ?? ''}
              onChange={(e) => {
                const selected = financialsubGroup?.find((item) => item.id === e.target.value);
                if (selected) setSubGroup(selected);
              }}
              sx={{ height: 32, fontSize: 14 }}
              renderValue={(selected: number | '') => {
                if (selected === '' || selected === undefined) {
                  return <em>{t('select.group')}</em>;
                }
                return selectedItem ? subGroup?.name : '';
              }}
            >
              <MenuItem value="">
                <em>{t('select.group')}</em>
              </MenuItem>
              {financialsubGroup &&
                financialsubGroup
                  .filter((type) => type.id !== selectedFinancialSubGroup?.id)
                  .map((item: SubGroup) => (
                    <MenuItem key={item.id} value={item.id} sx={{ fontSize: 13 }}>
                      {item.name}
                    </MenuItem>
                  ))}
            </Select>
          </FormControl>
        </Box>
      </Box>
      <Box sx={{ mt: 'auto', mb: 4 }}>
        <DrawerButtons
          onSave={() => {
            handleSubmit();
          }}
          onCancel={handleCancel}
          labelSave={t('common:transfer') as string}
        />
      </Box>
    </Box>
  );
}
